import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
    computed,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { MarketGroupConfig } from '@cds/betting-offer/grouping/bet-builder';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOptionGroup, EventSitemapSet, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { AngstromTagIdValues } from '@frontend/sports/betting-offer/feature/types';
import { BetBuilderConfig, Sitecore, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ComponentProxy, ComponentProxyDirective } from '@frontend/sports/deferred/feature/component-loader';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { BottomOffsetService } from '@frontend/sports/layout/feature';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { PopupManager } from '@frontend/sports/popup/feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Store } from '@ngrx/store';
import { debounce, isEmpty } from 'lodash-es';
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar';
import { firstValueFrom } from 'rxjs';

import { IBetslipRootState } from '../../../../betslip/base/store/state';
import { selectIsQuickBetActive } from '../../../../betslip/modules/quick-bet/quick-bet.selectors';
import { BetslipBarComponentBindings, BetslipDigitalComponentLoaderService } from '../../../betslip/betslip-digital-component-loader.service';
import { BottomNavActionsService } from '../../../bottom-nav/bottom-nav-actions.service';
import { ErrorComponent } from '../../../common/error/error.component';
import { EventNavigation } from '../../../event-details-common/event-details-dispatcher-events';
import { EventDetailsService } from '../../../event-details-common/event-details.service';
import { OptionGroupListComponent } from '../../../option-group/option-group-list.component';
import { BetBuilderTrackingValues, TrackBetBuilderSwitcherClick } from '../../bet-builder-tracking';
import { BetBuilderEventModel, MarketTab } from '../../model/bet-builder-event-model';
import { BetBuilderConfigService } from '../../services/bet-builder-config.service';
import { BetBuilderOverlayService } from '../../services/bet-builder-overlay.service';
import { MarketBuilderService } from '../../services/sportcast/market-builder.service';
import { BetBuilderOnboardingMessageService } from '../event-details-onboarding/bet-builder-onboarding-message.service';
import { MarketListHeaderComponent } from '../market-list-header/market-list-header.component';

// scrollbar default bottom offset
const scrollBottomOffset = 50;
@Component({
    selector: 'ms-betbuilder-market-list',
    templateUrl: './bet-builder-market-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgScrollbarModule, ErrorComponent, ComponentProxyDirective, MarketListHeaderComponent, OptionGroupListComponent],
})
export class BetbuilderMarketListComponent implements OnChanges, OnDestroy, OnInit {
    @HostBinding('class') className = 'ogp-bet-builder';
    @Input() eventData: EventModel | BetBuilderEventModel;
    @Input() source?: string;
    @ViewChild(NgScrollbar, { static: true }) scrollbarRef: NgScrollbar;
    @Input() isOverlay = false;
    quickBetComponent: ComponentProxy;
    betBarComponent: ComponentProxy<BetslipBarComponentBindings>;

    optionGroups: EventOptionGroup[];
    initialized: boolean;
    betBuilderTrackingValues = BetBuilderTrackingValues;
    event: EventModel;
    showOnboardingMessage = false;
    defaultExpandedMarketsCount: number;
    marketTabs: MarketTab[] = [];
    availableMarketGroups: { [key: string]: number } | undefined;
    totalMarketGroupConfig: MarketGroupConfig;
    private subscription?: EventSubscription<EventModel>;
    private marketGroupId: string;
    private readonly destroyRef = inject(DestroyRef);

    readonly isQuickBetActive = this.store.selectSignal(selectIsQuickBetActive);
    protected bottomOffset = computed<string>(() =>
        this.bottomOffsetService.bottomOffset() ? `${this.bottomOffsetService.bottomOffset() - scrollBottomOffset}` : '',
    );

    constructor(
        public sitecore: Sitecore,
        private marketListBuilderService: MarketBuilderService,
        private betBuilderConfigService: BetBuilderConfigService,
        private store: Store<IBetslipRootState>,
        private changeRef: ChangeDetectorRef,
        private tracker: TrackingService,
        private scrollContainer: ScrollContainerService,
        private dispatcher: DispatcherService,
        private fixtureFactory: FixtureFactory,
        private betBuilderConfig: BetBuilderConfig,
        private eventDetailsService: EventDetailsService,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
        private eventSubscriptionService: DetailedEventSubscriptionService,
        @Optional() private onboardingMessageService: BetBuilderOnboardingMessageService,
        @Optional() betslipLoader: BetslipDigitalComponentLoaderService,
        private offerGroupingService: OfferGroupingService,
        private bottomNavActionsService: BottomNavActionsService,
        private bottomOffsetService: BottomOffsetService,
        private betBuilderOverlayService: BetBuilderOverlayService,
        private popupManager: PopupManager,
    ) {
        if (betslipLoader) {
            this.quickBetComponent = betslipLoader.getQuickBetComponent();
            this.betBarComponent = betslipLoader.getBetslipBarComponent();

            this.betBarComponent.subscribeToOutputs({
                barClick: () => this.toggleBetslip(),
                myBetsClick: () => this.bottomNavActionsService.myBetsToggled(),
            });
        }
    }

    async ngOnInit(): Promise<void> {
        this.defaultExpandedMarketsCount = this.isOverlay
            ? this.betBuilderConfig.defaultSGPOverlayExpandedMarketsCount
            : this.betBuilderConfig.defaultSGPExpandedMarketsCount;

        if (!this.initialized) {
            await this.trySetEvent();
            this.getMarketGroupConfig();
        }

        this.dispatcher
            .on<string>(EventNavigation.SelectSubTab)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((tabId) => {
                this.selectGroup(tabId);
            });

        this.tracker.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.source,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.eventData.name,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.eventData.name,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.SPORT_ID]: this.eventData.sport.id.toString(),
            [trackingConstants.SPORT_LEAGUE_ID]: this.eventData.league.id.toString(),
        });
    }

    async ngOnChanges(): Promise<void> {
        await this.trySetEvent();
        this.getMarketGroupConfig();
        this.initialized = true;
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    selectGroup(marketGroupId: string, shouldCreateSubTabs?: boolean): void {
        if (this.sgpSet) {
            this.optionGroups = this.eventDetailsService.prepareEventOptionGroups({
                event: this.event,
                activeTab: marketGroupId,
                groupedSetsIds: this.sgpSet.groups,
                setTags: [marketGroupId],
                isSgp: true,
            });
            this.changeRef.markForCheck();
        } else {
            this.selectSportcastGroup(marketGroupId, shouldCreateSubTabs);
        }

        this.marketGroupId = marketGroupId;
    }

    private toggleBetslip() {
        if (this.popupManager.isPopupInStack('betslip')) {
            this.betBuilderOverlayService.close();
        } else {
            this.betBuilderOverlayService.dismissConfirmationScreen();
            this.popupManager.open('betslip');
        }
    }

    private selectSportcastGroup(marketGroupId: string, shouldCreateSubTabs?: boolean): void {
        if (!this.eventData.sportcastId) {
            this.optionGroups = [];

            return;
        }
        const fixtureId = this.event ? undefined : this.eventData.id;
        this.marketListBuilderService
            .getMarketItems(this.eventData.sport.id, this.eventData.sportcastId, this.eventData.league.id, marketGroupId, fixtureId)
            .subscribe(async (betBuilderViewModel) => {
                const betBuilderView = await betBuilderViewModel;
                this.availableMarketGroups = betBuilderView.availableMarketGroups;
                this.optionGroups = betBuilderView.eventOptionGroups;
                this.onboardingMessageService?.sportsCastVisible(!isEmpty(this.optionGroups) && this.isEmbeddedUi);

                if (shouldCreateSubTabs) {
                    this.createSubTabs();
                }
                if (!this.event) {
                    const fixture = betBuilderView.fixture;
                    if (fixture) {
                        this.event = this.fixtureFactory.create(fixture);
                    }
                }

                this.changeRef.markForCheck();

                if (this.inOverlay) {
                    this.scrollbarRef.scrollTo({
                        top: 0,
                    });
                } else {
                    this.scrollContainer.scrollTo(0, 0);
                }
            });
    }

    get sourceOfPick(): SourceOfPick {
        if (this.source === BetBuilderTrackingValues.EventDetails) {
            return SourceOfPick.EmbeddedBetBuilder;
        }
        if (this.inOverlay && this.source !== BetBuilderTrackingValues.EventDetails) {
            return SourceOfPick.OverlayBetBuilder;
        }

        return SourceOfPick.Other;
    }

    get inOverlay(): boolean {
        return this.betBuilderConfigService.showEntainUiInOverlay(this.eventData.sport.id) && !this.isEmbeddedUi;
    }

    get isEmbeddedUi(): boolean {
        return this.betBuilderConfigService.showEmbeddedUi() && this.source === BetBuilderTrackingValues.EventDetails;
    }

    get sgpSet(): EventSitemapSet | undefined {
        const sgp = this.event?.sitemapSets?.find((set) => set.id === AngstromTagIdValues.Sgp);

        if (this.eventData.betBuilderInfo?.betBuilderId && sgp) {
            return sgp;
        }

        return;
    }

    private getMarketGroupConfig(): void {
        if (this.sgpSet) {
            this.marketTabs = this.sgpSet.children?.map((x) => ({ id: x.id.toString(), name: x.name })) || [];
            this.tryOrderTabs();
            this.selectGroup(this.marketTabs[0]?.id);
            this.subscription?.unsubscribe();
            this.subscription = this.eventSubscriptionService.subscribe([
                {
                    event: this.event,
                    callback: debounce(this.eventChanged, 100),
                },
            ]);

            return;
        }

        this.marketListBuilderService.getMarketGroupConfig(this.eventData.sport.id, 'any').then((config) => {
            this.totalMarketGroupConfig = config;
            if (this.totalMarketGroupConfig.marketGroups.length) {
                this.selectGroup(config.marketGroups[0].id, true);
            }
        });
    }

    private createSubTabs(): void {
        this.marketTabs =
            this.totalMarketGroupConfig.marketGroups
                .filter((a) => this.availableMarketGroups?.hasOwnProperty(a.id))
                .map((x) => ({ id: x.id, name: x.name })) || [];

        if (!this.inOverlay && this.marketTabs.length) {
            const tabsPayload = {
                tabId: SetTab.BetBuilder,
                subTabs: this.marketTabs,
            };

            this.dispatcher.dispatch(EventNavigation.SetSubTabs, tabsPayload);
        }
    }

    private tryOrderTabs(): void {
        const sgpOrder = this.sitecoreEventDetails.sitemapOrder
            ?.find((s) => s.key === `${this.event.sport.id}`)
            ?.children?.find((c) => c.key === AngstromTagIdValues.Sgp.toLowerCase())?.children;

        if (sgpOrder?.length) {
            this.marketTabs.sort((a, b) => {
                const indexA = sgpOrder.findIndex((o) => o.key.toLowerCase() === a.id.toLowerCase());
                const indexB = sgpOrder.findIndex((o) => o.key.toLowerCase() === b.id.toLowerCase());

                return indexA - indexB;
            });
        }
    }

    private async trySetEvent(): Promise<void> {
        const sources = [BetBuilderTrackingValues.EventInfo, BetBuilderTrackingValues.BuildaBetButton, BetBuilderTrackingValues.EditPickButton];
        const shouldEnrichEvent =
            sources.includes(this.source as BetBuilderTrackingValues) &&
            !!this.eventData.betBuilderInfo?.betBuilderId &&
            this.eventData.betBuilderInfo?.betBuilderProvider !== BetBuilderProvider.Default;

        if (this.eventData instanceof EventModel && !shouldEnrichEvent) {
            this.event = this.eventData;

            return;
        }

        if (shouldEnrichEvent) {
            const groupingInfo = await firstValueFrom(this.offerGroupingService.getFixtureGrouping(this.eventData.sport.id, 'any'));
            const sgpGroupId = Object.values(groupingInfo?.marketGroupsV2 ?? []).find((g) => g.tag?.key === AngstromTagIdValues.Sgp)?.id;
            if (!sgpGroupId) {
                this.optionGroups = [];
                this.changeRef.markForCheck();

                throw new Error('No SGP group found in the market group configuration.');
            }

            const [event] = await Promise.all([
                // getEvent is used as it provides us with the market tabs and the option groups we need in the overlay
                this.eventDetailsService.getEvent(this.eventData.id, false, undefined, sgpGroupId),
                firstValueFrom(this.sitecoreEventDetails.whenReady),
            ]);
            if (event && !this.event) {
                this.event = event;
            }
        }
    }

    private eventChanged = (event: EventModel) => {
        this.event = event;
        this.marketTabs = this.sgpSet?.children?.map((x) => ({ id: x.id.toString(), name: x.name })) || [];
        if (!this.marketTabs.some((t) => t.id === this.marketGroupId)) {
            this.marketGroupId = this.marketTabs[0]?.id;
        }

        if (!this.marketGroupId) {
            this.optionGroups = [];
            this.changeRef.markForCheck();

            return;
        }

        this.selectGroup(this.marketGroupId);
    };

    tabClickedEventHandler(marketGroupId: string) {
        this.selectGroup(marketGroupId);
        this.trackSwitcherClickEvent(marketGroupId);
    }

    private trackSwitcherClickEvent(marketGroupId: string) {
        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetBuilderTrackingValues.OverlaySubNaviagtion,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.eventData.name,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.marketTabs.find((x) => x.id === marketGroupId)?.name,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.SPORT_ID]: this.eventData.sport.id.toString(),
            [trackingConstants.SPORT_LEAGUE_ID]: this.eventData.league.id.toString(),
        } as TrackBetBuilderSwitcherClick);
    }
}
